import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';
import { InferType } from 'yup';

import { enqueueSnackbar, FormProvider, RHFAutocomplete, RHFTextField, useDefaultEnumsStore } from '@zeta-core';
import { countries } from '@zeta-core/assets';
import { AdvisorAPIs, getErrorMessage } from '@zeta-services';
import { IGenericErrorResponse, IRegisterPartnerRequest } from '@zeta-types';
import { IJurisdictionType } from '../../../../lib/helpers';
import { addPartnerDefaultValues, addPartnerSchema } from './form-helpers';

const AddPartnerDialog = ({
  open,
  onClose,
  clientID,
  sourceControlMutateAsync,
  refetch,
  getDropdownOptions,
}: {
  open: boolean;
  onClose: () => void;
  clientID: string;
  sourceControlMutateAsync: UseMutateAsyncFunction<AxiosResponse<any>, Error, any, unknown>;
  refetch: any;
  getDropdownOptions: any;
}) => {
  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const titleOptions = defaultEnums?.titleType?.map((e) => e.titleType) ?? [];

  type SchemaType = InferType<typeof addPartnerSchema>;

  const methods = useForm<SchemaType>({
    defaultValues: addPartnerDefaultValues,
    resolver: yupResolver(addPartnerSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const capturePartnerDetails = useMutation({
    mutationFn: (payload: IRegisterPartnerRequest) => AdvisorAPIs().registerPartner(payload),
    onSuccess: (response: AxiosResponse<{ id: string }>) => {
      if (response.status === 200) {
        refetch?.();
        getDropdownOptions();
        sourceControlMutateAsync({ clientID: clientID });
        enqueueSnackbar('Partner details saved successfully', { variant: 'success' });
        methods.reset();
        onClose();
      }
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const onSubmit = (data: SchemaType) => {
    const payload = {
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      title: data.title,
      email: data.email,
      countryCode: '+' + data.countryCode.phone,
      mobile: data.mobile,
      dateOfBirth: null,
      currentAddress: {},
      primaryClientID: clientID,
      sourceType: 'Manual',
    };
    capturePartnerDetails.mutateAsync(payload);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose} maxWidth="md">
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle>
          <Typography variant="h4">Add Partner</Typography>
        </DialogTitle>

        <DialogContent sx={{ paddingTop: '16px !important' }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  sm: 2
                }}>
                <RHFAutocomplete name="title" label="Title*" options={titleOptions} />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid
                size={{
                  sm: 12
                }}>
                <RHFTextField name="firstName" label="First Name*" />
              </Grid>
              <Grid
                size={{
                  sm: 12
                }}>
                <RHFTextField name="middleName" label="Middle Name" />
              </Grid>
              <Grid
                size={{
                  sm: 12
                }}>
                <RHFTextField name="lastName" label="Last Name*" />
              </Grid>
              <Grid
                size={{
                  sm: 12
                }}>
                <RHFTextField name="email" label="Email Address*" />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <RHFAutocomplete
                  name="countryCode"
                  label="Code*"
                  options={countries}
                  isOptionEqualToValue={(option, value) => option?.phone === value?.phone}
                  getOptionLabel={(option: string | IJurisdictionType): string => {
                    if (typeof option === 'string') {
                      return option;
                    }

                    return option.phone && option.phone.length > 0 ? `+${option.phone} (${option.label})` : '';
                  }}
                  shouldDirty={true}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 10
                }}>
                <RHFTextField name="mobile" label="Mobile Number*" />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => {
              methods.reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <LoadingButton variant="contained" color="inherit" type="submit" loading={capturePartnerDetails.isPending}>
            Add Partner
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default AddPartnerDialog;
